import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// UX-S1 — In-app replacement for the native browser alert. Matches the
// existing `.snack` look already used in ProjectDetailPage (bottom-
// center, dark pill, red on error). 3-second auto-dismiss; multiple
// calls queue and render stacked.

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children, autoDismissMs = 3000 }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const h = timers.current.get(id);
    if (h) { clearTimeout(h); timers.current.delete(id); }
  }, []);

  const toast = useCallback((message, options = {}) => {
    const id = nextId++;
    const variant = options.variant === 'error' ? 'error' : 'success';
    setToasts((cur) => [...cur, { id, message, variant }]);
    const h = setTimeout(() => remove(id), options.durationMs || autoDismissMs);
    timers.current.set(id, h);
    return id;
  }, [autoDismissMs, remove]);

  // Clear pending timers on unmount.
  useEffect(() => () => {
    timers.current.forEach((h) => clearTimeout(h));
    timers.current.clear();
  }, []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className="ux-toast-host"
        data-testid="ux-toast-host"
        style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', gap: 6, zIndex: 2100,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`ux-toast ${t.variant}`}
            data-testid={`ux-toast-${t.variant}`}
            role={t.variant === 'error' ? 'alert' : 'status'}
            onClick={() => remove(t.id)}
            style={{
              background: t.variant === 'error' ? '#c0392b' : '#333',
              color: '#fff', padding: '10px 18px', borderRadius: 4,
              fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              maxWidth: '90vw', whiteSpace: 'pre-wrap', pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// useToast() returns `toast(message, { variant })`. A missing provider
// is a wiring bug — we throw so it's loud rather than silently swallowing
// the message.
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() requires a <ToastProvider> ancestor.');
  }
  return ctx;
}
